import React, { useEffect, useState } from 'react';
import lucasImg from '../assets/lucas.png';
import carlosImg from '../assets/carlos.png';
import maxiImg from '../assets/maxi.png';
import samuImg from '../assets/samu.png';
import placeholderImg from '../assets/placeholder-avatar.svg';
import renzoImg from '../assets/renzo.png';
import { Helmet } from 'react-helmet-async';
import Footer from '../components/sections/Footer';
import TrackedLink from '../components/global/TrackedLink';
import { CTA_IDS, CTA_LOCATIONS } from '../lib/analytics.constants';
import client, { urlFor } from '../config/sanityClient';

// Datos por defecto: se usan mientras no haya miembros cargados en Sanity.
const fallbackTeam = [
  {
    name: 'Lucas Fronciani',
    role: 'Presidente & Fundador',
    image: lucasImg,
    description: 'Lidera eien con foco en producto, arquitectura y dirección técnica. Apasionado por la tecnología desde muy joven, Lucas lidera eien con una visión clara: crear soluciones digitales que hagan crecer negocios de verdad. Con background en web y mobile, hoy se enfoca en producto, arquitectura y dirección técnica de proyectos propios y de clientes. Se destaca por simplificar lo complejo y acompañar de cerca cada proyecto. Para Lucas, cada cliente es un aliado, y cada idea una oportunidad de impacto real.',
    tags: ['Producto', 'Estrategia'],
  },
  {
    name: 'Carlos "Chucho" Báez',
    role: 'Vicepresidente & Co-fundador',
    image: carlosImg,
    description: 'Chucho es cofundador de eien y uno de los pilares técnicos detrás de cada proyecto. Con experiencia en desarrollo web, apps y sistemas, combina visión técnica y criterio de producto para construir soluciones funcionales, escalables y centradas en negocio. Lidera con claridad y compromiso, cuidando que cada decisión técnica esté alineada a los objetivos del cliente.',
    tags: ['Arquitectura', 'Full Stack'],
  },
  {
    name: 'Maximiliano Sánchez',
    role: 'Desarrollador de Software',
    image: maxiImg,
    description: 'Le apasiona la libertad de probar nuevas tecnologías y encontrar formas distintas de resolver los mismos problemas. Para Maxi, lo divertido del desarrollo es que no existe una única solución.',
    highlights: [
      { label: 'Stack Fav:', value: 'Django · React · Linux' },
      { label: 'Entra en Flow con:', value: 'Rap 80s/90s o podcasts' },
      { label: 'Quiere explorar:', value: 'Hardware · software integrations' },
    ],
    funFact: 'Fan de Olimpia y Spiderman',
    tags: ['Full Stack', 'Linux'],
  },
  {
    name: 'Gustavo Britez',
    role: 'Desarrollador de Software',
    image: placeholderImg,
    description: 'No se limita a escribir código: acompaña cada idea desde su nacimiento hasta convertirla en algo tangible, siendo versátil en los roles que el proyecto necesite. Plasmar una idea simple en algo estructurado y real es su motor.',
    highlights: [
      { label: 'Stack Fav:', value: 'JavaScript y sus frameworks' },
      { label: 'Entra en Flow con:', value: 'Tareas ordenadas por energía' },
      { label: 'Quiere explorar:', value: 'Ciberseguridad · infraestructura' },
    ],
    funFact: 'Estudió 3 años Ingeniería Eléctrica antes de pasarse a Informática',
    tags: ['Full Stack', 'Versatilidad'],
  },
  {
    name: 'Samuel Barboza',
    role: 'Desarrollador de Software',
    image: samuImg,
    description: 'Vive el trabajo en eien como un grupo de amigos haciendo cosas productivas — con libertad y con consciencia de la responsabilidad que eso conlleva. Cree que el criterio propio y el aprendizaje continuo son insustituibles.',
    highlights: [
      { label: 'Stack Fav:', value: 'Python · Django' },
      { label: 'Entra en Flow con:', value: 'Tecno, rock o metal · noche' },
      { label: 'Quiere aprender a:', value: 'Cocinar de todo' },
    ],
    funFact: 'Baterista: su cerebro hace ritmos 24/7, y marca sin querer con manos y pies',
    tags: ['Backend', 'Python'],
  },
  {
    name: 'Renzo Viola',
    role: 'Desarrollador de Software',
    image: renzoImg,
    description: 'Valora el ambiente y la metodología de eien. TypeScript es su herramienta favorita porque simplifica el trabajo, y lo que más lo motiva es ver el resultado final de lo que construye.',
    highlights: [
      { label: 'Stack Fav:', value: 'TypeScript' },
      { label: 'Entra en Flow con:', value: 'Música de fondo siempre' },
      { label: 'Quiere aprender a:', value: 'Tocar la guitarra' },
    ],
    funFact: 'Sigue comprando figuritas',
    tags: ['Frontend', 'TypeScript'],
  },
];

const diferencias = [
  {
    title: "Partnership real",
    description: "Somos parte de tu equipo: pensamos, priorizamos y construimos con vos. No somos un proveedor externo.",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
  },
  {
    title: "Modelos que se adaptan a vos",
    description: "Trabajamos por Proyecto, como Partnership continuo o con Revenue Share (1–18%), según lo que tu negocio necesite.",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    title: "Playbooks técnicos reutilizables",
    description: "Arquitectura, CI/CD y QA. Procesos probados que aceleran el desarrollo y mejoran la calidad.",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
  },
  {
    title: "Time-to-market con sprints",
    description: "Entregas incrementales y roadmap claro. El tiempo se define según el alcance y la complejidad del proyecto.",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
  },
  {
    title: "Escalamiento con datos",
    description: "Decisiones guiadas por métricas, costos y seguridad. Construimos para crecer de forma sostenible.",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
  },
];

const valores = [
  'Innovación',
  'Transparencia',
  'Cercanía con el cliente',
  'Pensamiento estratégico',
  'Calidad sobre cantidad',
  'Compromiso real',
];

const TeamCard = ({ person, minH = 'min-h-[200px]' }) => (
  <div className={`relative bg-gray-50 dark:bg-[#111111] border border-gray-100 dark:border-white/[0.07] hover:border-[#11b36d]/30 rounded-2xl overflow-hidden transition-colors duration-300 flex ${minH}`}>
    {/* Imagen izquierda */}
    <div className="relative w-36 sm:w-44 flex-shrink-0 self-stretch">
      <img
        src={person.image}
        alt={person.name}
        className={`absolute bottom-0 left-0 w-full h-full ${person.imgClass || 'object-cover object-top'}`}
      />
      <div className="absolute inset-y-0 right-0 w-12 bg-gradient-to-r from-transparent to-gray-50 dark:to-[#111111]" />
    </div>
    {/* Contenido derecho */}
    <div className="flex flex-col p-5 flex-1 min-w-0">
      <h3 className="text-lg sm:text-xl font-bold text-[#0b2b40] dark:text-white leading-tight mb-2">{person.name}</h3>
      <span className="w-fit text-[9px] font-semibold uppercase tracking-[0.15em] text-[#11b36d] border border-[#11b36d]/40 bg-[#11b36d]/10 rounded-full px-3 py-1 mb-3">{person.role}</span>
      <p className="text-slate-500 dark:text-slate-400 text-xs leading-relaxed mb-3">{person.description}</p>
      {person.highlights && (
        <div className="flex flex-col gap-2 mb-3">
          {person.highlights.map((h, i) => (
            <div key={i}>
              <p className="text-[10px] uppercase tracking-widest text-slate-300 dark:text-slate-600 mb-0.5">{h.label}</p>
              <p className="text-xs text-[#0b2b40] dark:text-white font-medium">{h.value}</p>
            </div>
          ))}
        </div>
      )}
      {person.funFact && (
        <div className="mt-auto pt-3 border-t border-gray-100 dark:border-white/[0.06]">
          <p className="text-[10px] uppercase tracking-widest text-slate-300 dark:text-slate-600 mb-0.5">Dato curioso</p>
          <p className="text-xs text-slate-400 dark:text-slate-500">{person.funFact}</p>
        </div>
      )}
    </div>
  </div>
);

const MeetTheTeam = ({ footerRef }) => {
  const [team, setTeam] = useState(fallbackTeam);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const query = `*[_type == "teamMembers" && isActive == true] | order(order asc, name asc) {
          _id,
          name,
          role,
          description,
          highlights,
          funFact,
          tags,
          image
        }`;
        const result = await client.fetch(query);
        if (result && result.length) {
          setTeam(
            result.map((m) => ({
              name: m.name,
              role: m.role,
              description: m.description,
              highlights: m.highlights,
              funFact: m.funFact,
              tags: m.tags,
              image: m.image ? urlFor(m.image).url() : placeholderImg,
            }))
          );
        }
      } catch (error) {
        console.error('Error fetching team members:', error);
      }
    };
    fetchTeam();
  }, []);

  return (
    <>
      <Helmet>
        <title>Conócenos · Empresa de desarrollo de software en Asunción, Paraguay | eien</title>
        <meta name="description" content="Conocé al equipo de eien, empresa paraguaya de desarrollo de software a medida en Asunción. Lucas Fronciani y Carlos Báez, cofundadores especializados en apps, SaaS y sistemas para empresas de Paraguay y LATAM." />
        <link rel="canonical" href="https://eientech.io/conocenos" />
      </Helmet>

      <div className="bg-gray-50 dark:bg-[#0a0a0a] min-h-screen">

        {/* Hero */}
        <section className="pt-24 pb-16 px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center">
            <div className="flex justify-center">
              <p className="w-fit text-[10px] font-medium uppercase tracking-[0.2em] text-[#11b36d] bg-[#11b36d]/10 border border-[#11b36d]/20 rounded-full px-3 py-1 mb-5">Quiénes somos</p>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-thin tracking-tight mb-6 text-[#0b2b40] dark:text-white">
              <span className="text-gradient text-short">Conócenos</span>
            </h1>
            <p className="text-xl text-slate-500 dark:text-slate-400 font-light leading-relaxed">
              En eien acompañamos tu idea y construimos la tecnología que la hace posible.
              Somos <span className="text-[#0b2b40] dark:text-white font-medium">tu aliado</span>, no un proveedor, con modelos{' '}
              <span className="text-[#11b36d] font-medium">Proyecto</span>,{' '}
              <span className="text-[#11b36d] font-medium">Partnership</span> y{' '}
              <span className="text-[#11b36d] font-medium">Revenue Share</span>.
            </p>
          </div>
        </section>

        {/* Nuestra Diferencia */}
        <section className="py-16 px-4 md:px-6 bg-white dark:bg-[#0d0d0d]">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <div className="flex justify-center">
                <p className="w-fit text-[10px] font-medium uppercase tracking-[0.2em] text-[#11b36d] bg-[#11b36d]/10 border border-[#11b36d]/20 rounded-full px-3 py-1 mb-5">Por qué eien</p>
              </div>
              <h2 className="text-3xl font-thin tracking-tight sm:text-4xl text-[#0b2b40] dark:text-white">
                Nuestra <span className="text-gradient text-short">diferencia</span>
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {diferencias.map((item, index) => (
                <div
                  key={index}
                  className="bg-gray-50 dark:bg-[#111111] border border-gray-100 dark:border-white/[0.07] hover:border-[#11b36d]/40 rounded-2xl p-6 transition-colors duration-300"
                >
                  <div className="flex items-start gap-4">
                    <div className="p-2.5 rounded-lg bg-[#11b36d]/10 border border-[#11b36d]/20 text-[#11b36d] flex-shrink-0">
                      {item.icon}
                    </div>
                    <div>
                      <h3 className="font-medium text-[#0b2b40] dark:text-white mb-2">{item.title}</h3>
                      <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Equipo */}
        <section className="py-16 px-4 md:px-6 bg-gray-50 dark:bg-[#0a0a0a]">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-thin tracking-tight sm:text-4xl text-[#0b2b40] dark:text-white">
                Un poco del <span className="text-gradient text-short">equipo eien</span>
              </h2>
            </div>
            <div className="flex flex-col gap-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {team.slice(0, 2).map((person, index) => (
                  <TeamCard key={index} person={person} minH="min-h-[360px]" />
                ))}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {team.slice(2).map((person, index) => (
                  <TeamCard key={index} person={person} />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Nuestra Historia */}
        <section className="py-16 px-4 md:px-6 bg-white dark:bg-[#0d0d0d]">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-8">
              <div className="flex justify-center">
                <p className="w-fit text-[10px] font-medium uppercase tracking-[0.2em] text-[#11b36d] bg-[#11b36d]/10 border border-[#11b36d]/20 rounded-full px-3 py-1 mb-5">Orígenes</p>
              </div>
              <h2 className="text-3xl font-thin tracking-tight sm:text-4xl text-[#0b2b40] dark:text-white">
                Nuestra <span className="text-gradient text-short">Historia</span>
              </h2>
            </div>
            <div className="bg-gray-50 dark:bg-[#111111] border border-gray-100 dark:border-white/[0.07] rounded-2xl p-8">
              <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed text-justify">
                eien nació hace más de 7 años como una idea ambiciosa:{' '}
                <span className="text-[#11b36d] font-medium">crear un espacio donde cualquier persona pudiera dar vida a lo que le apasiona</span>.
                Una visión donde <span className="italic">"la idea"</span>, esa chispa de inspiración, pudiera transformarse en algo real y poderoso, sin límites.
                Fue así como <span className="text-[#0b2b40] dark:text-white font-medium">Lucas</span> y{' '}
                <span className="text-[#0b2b40] dark:text-white font-medium">Chucho</span>, dos apasionados por la tecnología y el emprendimiento, decidieron unir fuerzas.
                Con trayectorias distintas pero valores compartidos, construyeron una empresa pensada para acompañar a startups y negocios en su crecimiento,
                brindando <span className="text-[#11b36d] font-medium">tecnología simple, clara y de alto impacto</span>.
                Lo que comenzó como un sueño hoy es una realidad: múltiples proyectos en marcha, productos propios y un equipo comprometido con transformar ideas en innovación.
              </p>
              <p className="text-[#11b36d] font-semibold text-center mt-6">Somos eien. Somos eternos.</p>
            </div>
          </div>
        </section>

        {/* Valores */}
        <section className="py-16 px-4 md:px-6 bg-gray-50 dark:bg-[#0a0a0a]">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <div className="flex justify-center">
                <p className="w-fit text-[10px] font-medium uppercase tracking-[0.2em] text-[#11b36d] bg-[#11b36d]/10 border border-[#11b36d]/20 rounded-full px-3 py-1 mb-5">Lo que nos mueve</p>
              </div>
              <h2 className="text-3xl font-thin tracking-tight sm:text-4xl text-[#0b2b40] dark:text-white">
                Nuestros <span className="text-gradient text-short">Valores</span>
              </h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {valores.map((value, i) => (
                <div
                  key={i}
                  className="bg-white dark:bg-[#111111] border border-gray-100 dark:border-white/[0.07] hover:border-[#11b36d]/40 rounded-xl px-5 py-4 text-center transition-colors duration-300"
                >
                  <span className="text-[#11b36d] text-lg mr-1.5">·</span>
                  <span className="text-sm font-medium text-[#0b2b40] dark:text-white">{value}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA final */}
        <section className="py-16 px-4 md:px-6 bg-white dark:bg-[#0d0d0d]">
          <div className="max-w-2xl mx-auto">
            <div className="bg-gray-50 dark:bg-[#111111] border border-gray-100 dark:border-white/[0.07] rounded-3xl p-10 text-center">
              <div className="flex justify-center">
                <p className="w-fit text-[10px] font-medium uppercase tracking-[0.2em] text-[#11b36d] bg-[#11b36d]/10 border border-[#11b36d]/20 rounded-full px-3 py-1 mb-5">Sin compromiso</p>
              </div>
              <h3 className="text-2xl md:text-3xl font-thin tracking-tight text-[#0b2b40] dark:text-white mb-4">
                ¿Querés trabajar con nosotros?
              </h3>
              <p className="text-slate-500 dark:text-slate-400 font-light mb-8">
                Contanos tu idea — te armamos roadmap, costo y plan de pagos en 48 h.
              </p>
              <TrackedLink to="/agendar" ctaId={CTA_IDS.TEAM_AGENDAR} ctaLocation={CTA_LOCATIONS.PAGE_CTA}>
                <button className="inline-flex items-center gap-2 bg-[#11b36d] text-white px-8 py-4 rounded-full font-semibold text-base hover:bg-[#0ea060] shadow-[0_2px_8px_rgba(17,179,109,0.22),inset_0_1px_0_rgba(255,255,255,0.12)] hover:shadow-[0_4px_14px_rgba(17,179,109,0.32),inset_0_1px_0_rgba(255,255,255,0.12)] transition-all duration-200">
                  Armar mi plan
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </button>
              </TrackedLink>
            </div>
          </div>
        </section>

        <section className="mt-8" id="footer" ref={footerRef}>
          <Footer />
        </section>
      </div>
    </>
  );
};

export default MeetTheTeam;
